import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EnvConfigurationService } from '../envconf.service';

export interface LoginResponseDto {
  access_token: string;
}

function jwtDecode(token: string): any {
  try {
    const payload = token.split('.')[1];
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class CustomAuthService {
  constructor(private router: Router) {}

  isAuthenticated(): boolean {
    return this.isLoggedIn();
  }

  setAuthentication(authResponse: LoginResponseDto): void {
    localStorage.setItem(EnvConfigurationService.configuration.KEY_TOKEN, JSON.stringify(authResponse));
  }

  private hasToken(): boolean {
    return localStorage.getItem(EnvConfigurationService.configuration.KEY_TOKEN) != null;
  }

  getToken(): string {
    return this.hasToken()
      ? JSON.parse(localStorage.getItem(EnvConfigurationService.configuration.KEY_TOKEN)).access_token
      : '';
  }

  getDecodedToken(): any {
    return this.hasToken() ? jwtDecode(this.getToken()) : '';
  }

  getAuthDecodedToken(): any {
    return this.hasToken() ? jwtDecode(this.getAuthToken()) : '';
  }

  isResetPassword(): boolean {
    return this.hasToken() ? this.getAuthDecodedToken().reset_password : false;
  }

  getUsername(): string {
    return this.hasToken() ? this.getDecodedToken().username : '';
  }

  getName(): string {
    if (!this.hasToken()) return '';
    const d = this.getDecodedToken();
    return d.name || d.username || '';
  }

  getUserId(): string {
    return this.hasToken() ? this.getDecodedToken().userId : '';
  }

  getRoles(): string[] {
    return this.hasToken()
      ? this.getDecodedToken().roles?.map((r: any) => r.authority) ?? []
      : [];
  }

  hasRole(roles: string[]): boolean {
    return this.hasToken() && this.getRoles().some(role => roles.includes(role));
  }

  /** Usuário interno (ROLE_MICHELIN na auth API): credenciais fora do portal, sem MFA nem troca de senha. */
  isInternalUser(): boolean {
    return this.hasToken() && (this.getAuthDecodedToken()?.roles ?? []).includes('ROLE_MICHELIN');
  }

  getAuthToken(): string {
    return this.hasToken() ? this.getDecodedToken().authToken ?? '' : '';
  }

  isLoggedIn(): boolean {
    if (!this.hasToken()) return false;
    if (this.getDecodedToken() === '') return false;
    return !this.isTokenExpired();
  }

  isTokenExpired(): boolean {
    const decoded = this.getDecodedToken();
    if (!decoded) return true;
    return decoded.exp < Math.floor(Date.now() / 1000);
  }

  /** Token guardado, porém vencido ou ilegível: a sessão foi perdida. */
  hasExpiredSession(): boolean {
    return this.hasToken() && !this.isLoggedIn();
  }

  /** Sessão perdida: volta ao login explicando o motivo (a tela de login exibe `LOGIN_ERROR_MESSAGE`). */
  expireSession(): void {
    localStorage.setItem('LOGIN_ERROR_MESSAGE', 'Sua sessão expirou. Faça login novamente.');
    this.logout();
  }

  logout(): void {
    localStorage.removeItem(EnvConfigurationService.configuration.KEY_TOKEN);
    this.router.navigate(['/login']);
  }
}
